import { Component, DestroyRef, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import {
  ControlValueAccessor,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { SelectModalData } from '@app/interfaces';
import { MessagesService } from '@app/services/messages.service';
import { DateParserFormatterYYYYMMDD, DatePickerAdapter } from '@app/utils/adaptaders/picker.adapter';
import { NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { AmKeyboardService } from '../am-keyboard/am-keyboard.service';
import { TranslateService } from '@ngx-translate/core';
import { GlobalsService } from '@app/services/micro/globals.service';
/**
 * Componente que representa un campo de entrada global personalizable para formularios.
 */
@Component({
    selector: 'am-input-global-form',
    templateUrl: './am-input-global-form.component.html',
    styleUrls: ['./am-input-global-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: AmInputGlobalFormComponent,
            multi: true,
        },
        { provide: NgbDateAdapter, useClass: DatePickerAdapter },
        { provide: NgbDateParserFormatter, useClass: DateParserFormatterYYYYMMDD },
    ],
    standalone: false
})

export class AmInputGlobalFormComponent implements ControlValueAccessor, OnChanges {

  private _valueChangeSub = new Subscription();

  @Input() errorMessages: {[key: string]: any} = {};

   /**
   * Referencia al elemento input del DOM.
   *
   * @type {ElementRef}
   */
   @ViewChild('inputElement') inputElement: ElementRef;
   /**
    * Mensaje de error cuando el valor no coincide con el patrón esperado.
    *
    * @type {string}
    * @default ''
    */
   @Input() errorPattern: string = '';

   @Input() disableToggle: boolean = false;
 
   /**
    * Mensaje de error cuando el campo es requerido pero no se ha ingresado ningún valor.
    *
    * @type {string}
    * @default ''
    */
   @Input() errorRequired: string = '';
 
   /**
    * Grupo de formulario al que pertenece el campo de entrada.
    *
    * @type {FormGroup}
    */
   @Input() formGroup: FormGroup;
 
   /**
    * Etiqueta del campo de entrada.
    *
    * @type {string}
    * @default ''
    */
   @Input() label: string = '';
 
   /**
    * Nombre del campo de entrada.
    *
    * @type {string}
    * @default ''
    */
   @Input() nameInput: string = '';
 
   /**
    * Texto de marcador de posición (placeholder) del campo de entrada.
    *
    * @type {string}
    * @default ''
    */
   @Input() placeholder: string = '';
 
   /**
    * Control del formulario asociado al campo de entrada.
    *
    * @type {any}
    */
   @Input() textControl: any;
 
   /**
    * Tipo de campo de entrada (text, number, email, etc.).
    *
    * @type {string}
    * @default ''
    */
   @Input() type: string = '';
 
   /**
    * Indica si el campo de entrada es de solo lectura.
    *
    * @type {boolean}
    * @default false
    */
   @Input() readOnly: boolean = false;
 
   /**
    * Indica si se debe mostrar un modal asociado al campo de entrada.
    *
    * @type {boolean}
    * @default false
    */
   @Input() showModal: boolean = false;

   /**
      Indica si el tema del scroll seria en blanco.
      @type {boolean}
    **/
    @Input() themeLightScroll: boolean = false;
 
   /**
    * Nombre de la lista asociada al campo de entrada (opcional).
    *
    * @type {any}
    */
   @Input() nameList: any;
 
   /**
    * Máscara para el campo de entrada (por ejemplo, para formatos de fecha o teléfono).
    *
    * @type {string}
    * @default ''
    */
   @Input() mask: string = '';
 
   /**
    * Ícono del botón asociado al campo de entrada.
    *
    * @type {string}
    * @default ''
    */
   @Input() iconButton: string = '';
 
   /**
    * Opciones para el modal de selección asociado al campo de entrada.
    *
    * @type {SelectModalData}
    */
   @Input() optionsSelect: SelectModalData;
 
   /**
    * Indica si el campo de entrada es un código de país.
    *
    * @type {boolean}
    * @default false
    */
   @Input() countryCode: boolean = false;
 
   /**
    * Longitud mínima permitida para el valor del campo de entrada.
    *
    * @type {string}
    * @default "1"
    */
   @Input() minlength = "1";
 
   /**
    * Longitud máxima permitida para el valor del campo de entrada.
    *
    * @type {string}
    * @default "100"
    */
   @Input() maxlength = "100";
 
   /**
    * Patrón de validación para el campo de entrada.
    *
    * @type {string}
    * @default ''
    */
   @Input() pattern = '';
 
   /**
    * Indica si se deben eliminar caracteres especiales al aplicar la máscara.
    *
    * @type {boolean}
    * @default true
    */
   @Input() dropSpecialCharacters: boolean = true;
 
   /**
    * Valor actual del campo de entrada.
    *
    * @type {any}
    */
   val: any;
 
   /**
    * Indica si el campo de entrada tiene el foco.
    *
    * @type {boolean}
    */
   focusInput: boolean = false;

  @Input() limitChars:number = 100;

  /** Indica si hay un error personalizado en el campo de entrada.
   *
   * @type {boolean}
   */
  @Input() hasErrorCustomError: boolean;

  /** Mensaje de error personalizado para el campo de entrada.
   *
   * @type {string}s
   */
  @Input() errorCustomError: string;

  @Input() isDisabled: boolean;

  constructor(
    private _messageServ: MessagesService,
    private _keyboardService: AmKeyboardService,
    private _localeServ: TranslateService,
    private _globalServ: GlobalsService,
    private destroyRef: DestroyRef
  ) {}

  onChange = (value: any) => { };
  onTouched = (value: any) => { };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['textControl']) {
      this._valueChangeSub.unsubscribe();
      if (this.textControl) {
        this._valueChangeSub = this.textControl.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((v: string | null) => {
            this.val = v;
            this.focusInput = !!v;
          });
      }
    }
  }

  /**
   * Método de la interfaz `ControlValueAccessor` que escribe un valor en el campo de entrada.
   *
   * @param {any} value - Valor a escribir en el campo.
   * @return {void} No retorna ningún valor.
   */
  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!this.inputElement) return;

    if (isDisabled) {
      this.inputElement.nativeElement.disabled = true;
    } else {
      this.inputElement.nativeElement.disabled = false;
    }
  }

  set value(val) {
    this.val = val
    this.onChange(val)
    if (val) {
      this.onTouched(val)
      this.focusInput = true;
    }
  }
  /**
   * Getter para el estilo del ícono del botón.
   *
   * @return {Object} Estilo CSS para el ícono del botón.
   */
  get getIconButton() {
    return { 'background': `center url(${this.iconButton}) no-repeat` }
  }

   /**
   * Método para enfocar el elemento input y abrir el modal de selección si está configurado.
   *
   * @param {Event} event - Evento que desencadena la acción (opcional).
   * @return {void} No retorna ningún valor.
   */
toggleDate(event?: Event) {
  if (this.readOnly || this.disableToggle || this.nameInput === 'nationality') {
    //Evita que el input reciba foco desde el click
    event?.preventDefault();
    event?.stopPropagation();
    return;
  }

  setTimeout(() => {
    this.inputElement.nativeElement.focus();
    this.secondChance(event);
  }, 80);

  this.focusInput = true;

  if (this.optionsSelect) {
    this.openModalList();
  }
}


  /**
   * Método que se ejecuta cuando el campo de entrada pierde el foco.
   * Actualiza el estado de `focusInput` en función del valor del campo.
   *
   * @return {void} No retorna ningún valor.
   */
  focusOutDate() {
    this.focusInput = this.val?.length > 0 ? true : false;
  }
 /**
   * Método para abrir un modal de selección y manejar la respuesta.
   *
   * @return {void} No retorna ningún valor.
   */
  openModalList(){
    if(this.readOnly) return;
    this._messageServ.showListSelect(this.optionsSelect)
      .then(res => {
        const currentLang = this._localeServ.currentLang.replace("-", "_");
      if(this.nameInput == 'contactEmergenCountryCode') {
        this._globalServ.setPhoneCode(res.country)
      }
        this.value = res[currentLang];
        if(this.countryCode){
          this.value = `+${res.countryCode}`
        }
        this._keyboardService.hide();
      })
      .catch(() => {
      })
      .finally(() => {
        this._keyboardService.hide();
      })
  }
    /**
   * Método para ajustar el desplazamiento del contenedor cuando el campo de entrada está activo.
   *
   * @param {Event} event - Evento que desencadena la acción.
   * @return {void} No retorna ningún valor.
   */
  secondChance(event) {
    if(event){
      const content = document.querySelector('.Layout-container--keyboard-active');
      const input = document.getElementById(this.inputElement.nativeElement.id);
      if (content) {
        const inputRect = input.getBoundingClientRect();
        const containerRect = content.getBoundingClientRect();

        const relativeY = (inputRect.top - 70) - containerRect.top + content.scrollTop;
        content.scrollTo({
          top: relativeY,
          behavior: 'smooth'
        });
      }
    }

  }
}
